import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Percent, CreditCard, Clock, Tag, Calendar, Store, Gift, Truck, Zap } from 'lucide-react';
import Badge from '../ui/Badge';
import { formatDiscountValue, formatDate, isDiscountExpiringSoon } from '../../utils/formatters';

const DiscountCard = ({ discount, showExpirationWarning = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [partnerLogoLoaded, setPartnerLogoLoaded] = useState(false);
  const [partnerLogoError, setPartnerLogoError] = useState(false);

  // Перевіряємо, чи знижка закінчується незабаром
  const isExpiring = showExpirationWarning && isDiscountExpiringSoon(discount.validUntil);
  
  // Іконки для різних типів знижок
  const discountIcon = {
    PERCENTAGE: <Percent size={16} />,
    FIXED: <CreditCard size={16} />,
    FREE_SHIPPING: <Truck size={16} />,
    BUY_ONE_GET_ONE: <Gift size={16} />,
    PROMOTIONAL: <Tag size={16} />,
    SPECIAL_OFFER: <Zap size={16} />
  };
  
  // Отримуємо відформатоване значення знижки
  const discountValue = formatDiscountValue(discount);

  return (
    <Link 
      to={`/discounts/${discount.id}`}
      className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col hover:shadow-lg transition-all duration-200 hover:-translate-y-1 block"
    >
      {/* Зображення знижки */}
      <div className="relative h-48">
        {discount.imageUrl && !imageError ? (
          <>
            {/* Placeholder поки зображення завантажується */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="animate-pulse">
                  <Tag size={32} className="text-gray-400" />
                </div>
              </div>
            )}
            <img
              src={discount.imageUrl}
              alt={discount.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(false);
              }}
            />
          </>
        ) : null}

        {/* Резервний блок, якщо зображення не завантажилось або відсутнє */}
        {(!discount.imageUrl || imageError) && (
          <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
            <Tag size={32} className="text-primary-400" />
          </div>
        )}
        
        {/* Бейдж для знижок, що закінчуються */}
        {isExpiring && (
          <div className="absolute top-2 right-2">
            <Badge variant="warning" className="text-xs">
              <Clock size={12} className="mr-1" />
              Скоро закінчиться
            </Badge>
          </div>
        )}
        
        {/* Градієнт та назва знижки */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent text-white p-4">
          <h3 className="text-lg font-bold leading-tight line-clamp-2">
            {discount.title}
          </h3>
        </div>
      </div>
      
      {/* Контент картки */}
      <div className="p-4 flex-grow flex flex-col">
        {/* Інформація про партнера */}
        <div className="flex items-center mb-3">
          {discount.partnerLogoUrl && !partnerLogoError ? (
            <>
              {!partnerLogoLoaded && (
                <div className="w-8 h-8 rounded-full mr-2 bg-gray-100 flex items-center justify-center animate-pulse">
                  <Store size={14} className="text-gray-400" />
                </div>
              )}
              <img
                src={discount.partnerLogoUrl}
                alt={discount.partnerName}
                className={`w-8 h-8 rounded-full mr-2 object-cover border border-gray-200 transition-opacity duration-300 ${
                  partnerLogoLoaded ? 'opacity-100' : 'opacity-0 absolute'
                }`}
                onLoad={() => setPartnerLogoLoaded(true)}
                onError={() => {
                  setPartnerLogoError(true);
                  setPartnerLogoLoaded(false);
                }}
              />
            </>
          ) : (
            <div className="w-8 h-8 rounded-full mr-2 bg-gray-100 flex items-center justify-center">
              <Store size={14} className="text-gray-400" />
            </div>
          )}
          
          <span className="text-sm text-gray-600 font-medium truncate">
            {discount.partnerName}
          </span>
        </div>
        
        {/* Опис знижки */}
        <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-grow">
          {discount.description || discount.terms || 'Детальна інформація доступна на сторінці знижки'}
        </p>
        
        {/* Нижня частина з інформацією про знижку */}
        <div className="mt-auto space-y-3">
          {/* Значення знижки */}
          <div className="flex items-center justify-between">
            <div className="flex items-center font-bold text-primary-600">
              <span className="mr-2">{discountIcon[discount.type] || discountIcon.PROMOTIONAL}</span>
              <span className="text-lg">{discountValue}</span>
            </div>
            
            {/* Дата закінчення */}
            {discount.validUntil && (
              <div className="flex items-center text-gray-500 text-xs">
                <Calendar size={12} className="mr-1" />
                <span>до {formatDate(discount.validUntil)}</span>
              </div>
            )}
          </div>
          
          {/* Категорія */}
          {discount.categoryName && (
            <div className="flex items-center text-xs text-gray-500">
              <Tag size={12} className="mr-1" />
              <span>{discount.categoryName}</span>
            </div>
          )}
          
          {/* Індикатор клікабельності замість кнопки */}
          <div className="text-center bg-primary-600 text-white py-2 px-4 rounded-md font-medium transition-colors duration-200 group-hover:bg-primary-700">
            Переглянути деталі
          </div>
        </div>
      </div>
    </Link>
  );
};

export default DiscountCard;
